"use client";

import Image from "next/image";
import React, { useState } from "react";

import ReviewDetailModal from "@/components/reviews/ReviewDetailModal";
import VerifiedTick from "@/components/reviews/VerifiedTick";
import { urlForImage } from "@/sanity/lib/image";
import type { Review, ReviewSource } from "@/app/(client)/reviews/page";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function StarsDisplay({ stars }: { stars: number }) {
  const n = Math.min(5, Math.max(0, stars));
  return (
    <span className="inline-flex text-amber-400" aria-label={`${n} stars`}>
      {"★".repeat(n)}
      {"☆".repeat(5 - n)}
    </span>
  );
}

interface ReviewGridProps {
  reviews: Review[];
  reviewSources: ReviewSource[];
}

export default function ReviewGrid({
  reviews,
  reviewSources,
}: ReviewGridProps) {
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleCardClick = (review: Review) => {
    setSelectedReview(review);
    setModalOpen(true);
  };

  return (
    <>
      <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {reviews.map((review) => {
          const avatarSrc = review.avatar
            ? urlForImage(review.avatar).width(64).height(64).url()
            : (review.avatarUrl ?? null);
          const avatarIsExternal = Boolean(review.avatarUrl && !review.avatar);
          const sourceConfig = review.source
            ? reviewSources.find(
                (s) => slugify(s.name) === review.source?.toLowerCase()
              )
            : null;
          const sourceLogoUrl =
            sourceConfig?.logo &&
            urlForImage(sourceConfig.logo).width(24).height(24).url();
          return (
            <li key={review._id} className="flex flex-col">
              <button
                type="button"
                onClick={() => handleCardClick(review)}
                className="flex flex-col rounded-xl border border-violet-200 bg-violet-50/30 p-5 text-left shadow-sm transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                data-speakable-review
                aria-label={`Read full review by ${review.name}`}
              >
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <StarsDisplay stars={review.stars ?? 0} />
                  {sourceLogoUrl ? (
                    <span
                      className="relative flex size-5 shrink-0"
                      aria-label={sourceConfig?.name}
                    >
                      <Image
                        src={sourceLogoUrl}
                        alt=""
                        width={20}
                        height={20}
                        className="object-contain"
                      />
                    </span>
                  ) : null}
                  {review.verified && <VerifiedTick />}
                </div>
                {review.title && (
                  <h3 className="font-semibold text-violet-950">
                    {review.title}
                  </h3>
                )}
                <p className="mt-2 flex-1 text-sm text-violet-800 line-clamp-4">
                  {review.context}
                </p>
                {review.tags && review.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {review.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded bg-violet-100 px-1.5 py-0.5 text-xs text-violet-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {avatarSrc ? (
                      <div className="relative size-8 shrink-0 overflow-hidden rounded-full bg-violet-200">
                        <Image
                          src={avatarSrc}
                          alt=""
                          width={32}
                          height={32}
                          className="object-cover"
                          unoptimized={avatarIsExternal}
                        />
                      </div>
                    ) : null}
                    <p className="truncate text-sm font-medium text-violet-900">
                      — {review.name}
                    </p>
                  </div>
                  {review.sourceUrl && (
                    <a
                      href={review.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 rounded p-1.5 text-violet-600 hover:bg-violet-100 hover:text-violet-800"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="View on source"
                    >
                      <svg
                        className="size-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      <ReviewDetailModal
        review={selectedReview}
        reviewSources={reviewSources}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </>
  );
}
